import { Client } from "@/Common/Api/Client";
import { PreventiveCareNotice } from "@/Common/Api/PreventiveCare/Dto/Responses/PreventiveCareNotice";
import { Guid } from "@/Common/Models/Guid";
import { PortfolioInboxes, PreventiveRecallTab } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { PreventiveRecallListDto } from "@/Portfolio/Models/PreventiveRecallListDto";
import { PortfolioStore } from "@/Portfolio/Stores/PortfolioStore";

class PreventiveRecallListHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.Portfolio.PreventiveRecallList_Reload, this.reload);
        this.RegisterOperation(LocalOperations.Portfolio.PreventiveRecallList_Load_From_Search, this.loadFromSearch);
        this.RegisterOperation(LocalOperations.Portfolio.PreventiveRecallList_NavigateBetweenTabs, this.navigateBetweenTabs);
        this.RegisterOperation(LocalOperations.Portfolio.PreventiveRecallList_SelectItem, this.selectItem);
        this.RegisterOperation(LocalOperations.Portfolio.PreventiveRecallList_SelectAllItems, this.selectAllItems);
        this.RegisterOperation(LocalOperations.Portfolio.PreventiveRecallList_SetPage, this.setPage);
        this.RegisterOperation(LocalOperations.Portfolio.PreventiveRecallList_UpdateLastCommunicationDate, this.updateLastCommunicationDate);
        this.RegisterOperation(LocalOperations.Portfolio.PreventiveRecallList_OnGenerateBonusReportClick, this.generateBonusReport);
        this.RegisterOperation(LocalOperations.Portfolio.PreventiveRecallList_GenerateBonusReport_Visibility, this.toggleGenerateBonusReportPopupVisibility);
        this.RegisterOperation(LocalOperations.Portfolio.PreventiveRecallList_InterruptGenerateBonusReportLoading, this.interruptGenerateBonusReportLoading);
        this.RegisterOperation(LocalOperations.Portfolio.PreventiveRecallList_LoadPreventiveCareNotices, this.loadPreventiveCareNotices);
    }

    private selectItem(request: LocalEvent, store: PortfolioStore): void {
        const itemId = request.Get<string>("id");
        const isSelected = request.Get<boolean>("isSelected");
        const recallList = store.preventiveRecallListModel.get("RecallList").toArray()
            .mutate(p => p.IsSelected = isSelected, p => p.Model.Id === itemId);

        store.preventiveRecallListModel = store.preventiveRecallListModel
            .set("RecallList", recallList.toImmutable())
            .set("IsAllItemsSelected", recallList.filter(p => !p.CheckboxDisabled).all(p => p.IsSelected));
    }

    private selectAllItems(request: LocalEvent, store: PortfolioStore): void {
        const isSelected = request.Get<boolean>("isSelected");
        const recallList = store.preventiveRecallListModel.get("RecallList").toArray()
            .mutate(p => {
                if (!p.CheckboxDisabled) {
                    p.IsSelected = isSelected;
                }
            });

        store.preventiveRecallListModel = store.preventiveRecallListModel
            .set("RecallList", recallList.toImmutable())
            .set("IsAllItemsSelected", isSelected);
    }

    private reload(_request: LocalEvent, store: PortfolioStore): void {
        if (portfolioStore.timeoutReferencePreventiveRecallList !== -1) {
            clearTimeout(portfolioStore.timeoutReferencePreventiveRecallList);
            portfolioStore.timeoutReferencePreventiveRecallList = -1;
        }

        portfolioStore.timeoutReferencePreventiveRecallList = window.setTimeout(() => {
            store.preventiveRecallListModel = store.preventiveRecallListModel.set("CurrentPage", 1);
            store.PortfolioActions.loadPreventiveRecallList();
        }, 250);
    }

    private loadFromSearch(request: LocalEvent, store: PortfolioStore): void {
        const searchText = request.Get<string>("searchText");
        store.preventiveRecallListModel = store.preventiveRecallListModel
            .set("CurrentPage", 1)
            .set("SearchText", searchText);

        portfolioStore.PortfolioActions.loadPreventiveRecallList();
    }

    private navigateBetweenTabs(request: LocalEvent, store: PortfolioStore): void {
        const currentTab = request.Get<PreventiveRecallTab>("currentTab");

        store.preventiveRecallListModel = store.preventiveRecallListModel.set("CurrentTab", currentTab);
        store.Model = store.Model.set("CurrentInbox", PortfolioInboxes.PreventiveRecallList);
    }

    private setPage(request: LocalEvent, store: PortfolioStore): void {
        const page = request.Get<number>("Page");
        store.preventiveRecallListModel = store.preventiveRecallListModel.set("CurrentPage", page);

        portfolioStore.PortfolioActions.loadPreventiveRecallList();
    }

    private async updateLastCommunicationDate(request: LocalEvent): Promise<void> {
        const preventiveCareNotices = request.Get<PreventiveCareNotice[]>("preventiveCareNotices");

        const res = await Client.postAsync(ServerOperations.Server.Myle_Portfolio_PreventiveCare_SaveNotice, { PreventiveCareNotices: preventiveCareNotices });

        if (res.isSuccess) {
            portfolioStore.PortfolioActions.loadPreventiveRecallList();
        }
    }

    private generateBonusReport(request: LocalEvent, store: PortfolioStore): void {
        const providerId = request.Get<Guid>("providerId");
        const criteriaId = request.Get<Guid>("criteriaId");
        store.preventiveRecallListModel = store.preventiveRecallListModel
            .set("IsGenerateBonusReportPopupOpen", false)
            .set("IsGenerateBonusReportLoading", true);

        store.PortfolioActions.generateBonusReport(providerId, criteriaId);
    }

    private toggleGenerateBonusReportPopupVisibility(request: LocalEvent, store: PortfolioStore): void {
        const isOpen = request.Get<boolean>("isOpen");

        store.preventiveRecallListModel = store.preventiveRecallListModel
            .set("IsGenerateBonusReportPopupOpen", isOpen);
    }

    private interruptGenerateBonusReportLoading(request: LocalEvent, store: PortfolioStore): void {
        store.preventiveRecallListModel = store.preventiveRecallListModel
            .set("IsGenerateBonusReportLoading", false);
    }

    private loadPreventiveCareNotices(request: LocalEvent, store: PortfolioStore): void {
        const model = request.Get<PreventiveRecallListDto>("model");
        store.PortfolioActions.loadPreventiveCareNotices(model.PatientContactFacetId, model.PreventiveCareCriteriaId, model.EmployeeContactFacetId);
    }
}

export { PreventiveRecallListHandler };
